import React, { useEffect, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Europe = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null); // null | "loading" | "success" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    document.title =
      "4keeperz — Europe Edition Coming Soon | Swiss Goalkeeper Gloves";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "4keeperz — Premium Swiss goalkeeper gloves. German Giga Grip latex. Europe edition launching 2026 — join the early-access list."
      );
    }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${BACKEND}/api/prelaunch`, {
        email: trimmed,
        interest: "Europe early access",
        source: "eu_parking",
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err.response?.data?.detail ||
          "Something went wrong. Please try again or email info@4keeperz.ch"
      );
    }
  };

  return (
    <div
      data-testid="europe-parking"
      className="relative min-h-screen flex flex-col items-center justify-center px-6 py-20 text-center overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse 800px 600px at 80% 20%, rgba(0,166,81,0.18), transparent 60%), radial-gradient(ellipse 600px 500px at 10% 90%, rgba(0,166,81,0.12), transparent 60%), linear-gradient(180deg, #000 0%, #050505 100%)",
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative z-10 max-w-3xl mx-auto">
        <div className="font-brush text-[#00A651] leading-none mb-4 text-6xl md:text-7xl">
          4keeperz
        </div>

        <div className="inline-flex items-center gap-2.5 border border-white/15 px-4 py-2 mb-7">
          <span className="w-1.5 h-1.5 bg-[#00A651] rounded-full animate-pulse" />
          <span className="font-condensed uppercase tracking-[0.3em] text-[11px] text-white/70">
            Europe Edition · Coming 2026
          </span>
        </div>

        <h1 className="font-brush text-white leading-[0.95] mb-6 text-5xl md:text-7xl">
          Built in Switzerland.
          <span className="block text-[#00A651]">Soon across Europe.</span>
        </h1>

        <p className="text-white/75 leading-relaxed max-w-xl mx-auto mb-10 text-base md:text-lg">
          Premium goalkeeper gloves with{" "}
          <strong className="text-white">4mm German Giga Grip latex</strong>,
          Breathapren body and Negative Cut. By keeperz for keeperz — no
          marketing tax, just pro material. We&apos;re currently shipping in
          Switzerland &amp; Liechtenstein. Drop your email to get{" "}
          <strong className="text-[#00A651]">early access</strong> when we open
          Europe-wide shipping.
        </p>

        {status === "success" ? (
          <div
            data-testid="europe-success"
            className="border border-[#00A651]/40 bg-[#00A651]/10 max-w-lg mx-auto mb-14 p-5 text-[#00A651]"
          >
            ✓ You&apos;re on the list. We&apos;ll reach out when Europe is live.
          </div>
        ) : (
          <>
            {status === "error" && (
              <div className="border border-red-500/40 bg-red-500/10 max-w-lg mx-auto mb-4 p-3 text-red-300 text-sm">
                {errorMsg}
              </div>
            )}
            <form
              onSubmit={onSubmit}
              data-testid="europe-signup-form"
              className="flex flex-col sm:flex-row gap-2 max-w-lg mx-auto mb-3 bg-[#0a0a0a] border border-white/10 p-2"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoComplete="email"
                data-testid="europe-email-input"
                className="flex-1 bg-transparent border-0 outline-none text-white px-4 py-3.5 placeholder-white/40"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="europe-submit-btn"
                className="bg-[#00A651] hover:bg-[#00C961] disabled:opacity-50 text-black font-condensed uppercase tracking-[0.2em] text-[13px] px-6 py-3.5 transition-colors whitespace-nowrap"
              >
                {status === "loading" ? "Sending..." : "Notify me"}
              </button>
            </form>
            <div className="text-xs text-white/40 mb-14">
              No spam. One email when Europe launches.
            </div>
          </>
        )}

        <a
          href="https://4keeperz.ch"
          data-testid="europe-ch-cta"
          className="inline-flex items-center gap-3 border border-white/20 hover:border-[#00A651] hover:text-[#00A651] text-white font-condensed uppercase tracking-[0.2em] text-[13px] px-7 py-3.5 transition-colors"
        >
          Shop now in Switzerland → 4keeperz.ch
        </a>
        <div className="text-xs text-white/40 mt-3">
          Already shipping CH &amp; FL · Free shipping over CHF 80
        </div>

        <div className="flex flex-wrap justify-center gap-6 mt-16 pt-10 border-t border-white/10 max-w-2xl mx-auto">
          {[
            "4mm German Latex",
            "Breathapren Body",
            "Pro Quality · Fair Price",
            "Swiss Brand",
          ].map((f) => (
            <div
              key={f}
              className="flex items-center gap-2 font-condensed uppercase tracking-[0.15em] text-xs text-white/50"
            >
              <span className="w-1.5 h-1.5 bg-[#00A651]" />
              {f}
            </div>
          ))}
        </div>

        <footer className="mt-20 text-[11px] text-white/30 font-condensed uppercase tracking-[0.2em]">
          © 2026 4keeperz · Eschenbach SZ · info@4keeperz.ch
        </footer>
      </div>
    </div>
  );
};

export default Europe;
